/** 内容库：标签筛选、文件夹、最近添加；文件夹里的内容。家长端不能新建文件夹 / 标签（与 iOS 一致） */
import { ChevronLeft, Folder as FolderIcon, LibraryBig } from "lucide-react";
import type { KeyboardEvent, ReactNode } from "react";
import { useMemo, useState } from "react";
import { type Content, useCalendar, useStore } from "../../core";
import { ChoiceChip, C, EmptyHint, IconButton, Page, SectionLabel } from "../../ui";
import { contentMeta, folderStyle, newestFirst, sortFolders, sortTags, typeBadge } from "./activity";
import { webModule } from "./modules";

/** 类型色块（PDF / 写字 / 视频 / H5） */
export function TypeBlock({ type, width = 44, height = 44, radius = 12, size = 12 }: { type: string; width?: number; height?: number; radius?: number; size?: number }) {
  const b = typeBadge(type);
  return (
    <span className="learn-type-block" style={{ width, height, borderRadius: radius, background: b.tint, color: b.ink, fontSize: size }}>
      {b.label}
    </span>
  );
}

/** 子页面顶部：返回 + 标题 */
export function BackHeader({ title, onBack, trailing }: { title: ReactNode; onBack: () => void; trailing?: ReactNode }) {
  return (
    <div className="learn-back-header">
      <IconButton label="返回" onClick={onBack}>
        <ChevronLeft size={22} strokeWidth={2.6} />
      </IconButton>
      <h1>{title}</h1>
      {trailing ?? <span style={{ width: 40 }} />}
    </div>
  );
}

function onEnter(fn: () => void) {
  return (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      fn();
    }
  };
}

/** 内容列表的一行：类型色块、标题、标签 / 时间、"布置" */
export function ContentRow({ content, onOpen, onAssign }: { content: Content; onOpen: () => void; onAssign?: () => void }) {
  const tags = useStore((s) => s.tags);
  const folders = useStore((s) => s.folders);
  const cal = useCalendar();
  return (
    <div className="learn-row" role="link" tabIndex={0} onClick={onOpen} onKeyDown={onEnter(onOpen)} data-testid="content-row">
      <TypeBlock type={content.activityType} />
      <div className="learn-row-main">
        <strong>{content.title}</strong>
        <span>{contentMeta(content, tags, folders, cal, webModule(content.activityType).summary?.(content) ?? null)}</span>
      </div>
      {onAssign && (
        <button
          type="button"
          className="learn-assign-btn"
          onClick={(e) => {
            e.stopPropagation();
            onAssign();
          }}
        >
          布置
        </button>
      )}
    </div>
  );
}

export function LibraryView({ onOpenFolder, onOpenContent, onAssign }: { onOpenFolder: (id: string) => void; onOpenContent: (id: string) => void; onAssign: (c: Content) => void }) {
  const [tagId, setTagId] = useState<string | null>(null);
  const tagsById = useStore((s) => s.tags);
  const foldersById = useStore((s) => s.folders);
  const contentsById = useStore((s) => s.contents);
  const tags = useMemo(() => sortTags(Object.values(tagsById)), [tagsById]);
  const folders = useMemo(() => sortFolders(Object.values(foldersById)), [foldersById]);
  const contents = useMemo(() => Object.values(contentsById).filter((c) => tagId == null || (c.tagIds ?? []).includes(tagId)), [contentsById, tagId]);
  const recent = useMemo(() => newestFirst(contents).slice(0, 12), [contents]);

  return (
    <>
      <div className="learn-tags" role="toolbar" aria-label="按标签筛选">
        <ChoiceChip selected={tagId == null} height={30} unselectedBg={C.surface} onClick={() => setTagId(null)}>
          全部
        </ChoiceChip>
        {tags.map((t) => (
          <ChoiceChip key={t.id} selected={tagId === t.id} height={30} unselectedBg={C.surface} onClick={() => setTagId(t.id)}>
            #{t.name}
          </ChoiceChip>
        ))}
      </div>
      {folders.length > 0 && (
        <div className="learn-folders">
          {folders.map((f, i) => {
            const st = folderStyle(i);
            return (
              <button key={f.id} type="button" className="learn-folder" onClick={() => onOpenFolder(f.id)}>
                <span className="learn-folder-icon" style={{ background: st.tint, color: st.ink }}>
                  <FolderIcon size={18} strokeWidth={2.4} />
                </span>
                <span className="learn-folder-text">
                  <strong>{f.name}</strong>
                  <small>{contents.filter((c) => c.folderId === f.id).length} 份</small>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <SectionLabel>最近添加</SectionLabel>
      {recent.length === 0 ? (
        <EmptyHint icon={LibraryBig} text={tagId ? "这个标签下还没有内容" : "内容库还是空的，点右上角上传"} />
      ) : (
        <div className="learn-list">
          {recent.map((c) => (
            <ContentRow key={c.id} content={c} onOpen={() => onOpenContent(c.id)} onAssign={() => onAssign(c)} />
          ))}
        </div>
      )}
    </>
  );
}

/** 文件夹里的内容 */
export function FolderView({ folderId, onBack, onOpenContent, onAssign }: { folderId: string; onBack: () => void; onOpenContent: (id: string) => void; onAssign: (c: Content) => void }) {
  const folder = useStore((s) => s.folders[folderId]);
  const contentsById = useStore((s) => s.contents);
  const list = useMemo(() => newestFirst(Object.values(contentsById).filter((c) => c.folderId === folderId)), [contentsById, folderId]);
  return (
    <Page>
      <BackHeader title={folder?.name ?? "文件夹"} onBack={onBack} />
      {list.length === 0 ? (
        <EmptyHint icon={FolderIcon} text="这个文件夹还是空的" />
      ) : (
        <div className="learn-list">
          {list.map((c) => (
            <ContentRow key={c.id} content={c} onOpen={() => onOpenContent(c.id)} onAssign={() => onAssign(c)} />
          ))}
        </div>
      )}
    </Page>
  );
}
